import { processFloorPlanPixels } from "./processFloorPlanPixels"

/**
 * @typedef {Object} ProcessedImageResult
 * @property {string} url - The image URL (blob URL if processed, raw URL if CORS fallback)
 * @property {boolean} isBlobUrl - Whether the URL is a blob URL that needs revoking
 */

/**
 * Loads a floor plan image, processes it on a canvas to make white pixels transparent
 * (with white halo preservation around dark lines), and returns a blob URL.
 *
 * Falls back to the raw URL if any processing error occurs (CORS, image load
 * failure, null canvas context, toBlob failure). Only AbortError is rethrown.
 * This ensures floor plans always render — unprocessed if necessary — rather
 * than showing an error toast when pixel processing fails.
 *
 * NOTE: A parallel implementation exists in floor_plan_source.js (loadAndProcessImage)
 * that returns data URLs instead of blob URLs for Map View's Mapbox image source.
 * ENG-4927 tracks unifying these two pipelines.
 *
 * @param {string} imageUrl - Presigned download URL for the floor plan image
 * @param {AbortSignal} [signal] - Optional abort signal for cancellation
 * @returns {Promise<ProcessedImageResult>} Processed image URL and blob status
 */
export async function loadFloorPlanImage(imageUrl, signal) {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError")
  }

  try {
    const img = await loadImage(imageUrl, signal)
    const blobUrl = await processImageOnCanvas(img)
    return { url: blobUrl, isBlobUrl: true }
  } catch (e) {
    if (e.name === "AbortError") throw e
    console.warn("Floor plan processing failed, using raw image:", e.message || e)
    return { url: imageUrl, isBlobUrl: false }
  }
}

/**
 * Loads an image element with CORS enabled.
 *
 * @param {string} url - Image URL
 * @param {AbortSignal} [signal] - Optional abort signal
 * @returns {Promise<HTMLImageElement>} Loaded image element
 */
function loadImage(url, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"))
      return
    }

    const img = new Image()
    img.crossOrigin = "anonymous"

    const onAbort = () => {
      img.src = ""
      reject(new DOMException("Aborted", "AbortError"))
    }

    signal?.addEventListener("abort", onAbort, { once: true })

    img.onload = () => {
      signal?.removeEventListener("abort", onAbort)
      resolve(img)
    }

    img.onerror = e => {
      signal?.removeEventListener("abort", onAbort)
      reject(e instanceof Error ? e : new Error("Image load failed"))
    }

    img.src = url
  })
}

/**
 * Draws an image to an offscreen canvas, processes pixels, and returns a blob URL.
 *
 * @param {HTMLImageElement} img - Loaded image element
 * @returns {Promise<string>} Blob URL of the processed image
 */
function processImageOnCanvas(img) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas")
    canvas.width = img.width
    canvas.height = img.height

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      reject(new Error("Canvas context unavailable"))
      return
    }
    ctx.drawImage(img, 0, 0)

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    processFloorPlanPixels(imageData)
    ctx.putImageData(imageData, 0, 0)

    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error("Canvas toBlob returned null"))
        return
      }
      resolve(URL.createObjectURL(blob))
    })
  })
}
